import { Sparkles, Check, ArrowRight, ExternalLink } from 'lucide-react';
import { BRAND } from '../lib/constants';
import { useRegistrationUrl } from '../lib/useSettings';

const BENEFITS = [
  { t: 'Premium Portfolio Page', d: 'A high-end editorial page that does justice to your craft — built for brides who scroll with intent.' },
  { t: 'Verified Badge', d: 'Earn the verified seal that converts. Our team personally reviews every application and badges authenticated artists.' },
  { t: 'Direct WhatsApp Leads', d: 'We send booking-ready enquiries straight to your WhatsApp — no middlemen, no chasing.' },
  { t: 'Editorial Features', d: 'Get featured in The Edit, our city-by-city journal of India\'s most sought-after MUAs.' },
  { t: 'No Registration Fee', d: 'Founding artists pay nothing. We earn only when you earn — keep your full pricing always.' },
  { t: 'Discovery That Works', d: 'Smart filtering by occasion, city, style and budget means brides who land on your page are ready to book.' },
];

const STEPS = [
  { n: '01', t: 'Submit Application', d: 'Fill out our quick registration form (2–3 minutes).' },
  { n: '02', t: 'Editorial Review', d: 'Our team reviews your portfolio within 48 hours.' },
  { n: '03', t: 'Verification Call', d: 'A short call to verify details and discuss your offering.' },
  { n: '04', t: 'Go Live', d: 'Your premium portfolio page launches — and the leads begin.' },
];

export default function RegisterMua() {
  const registrationUrl = useRegistrationUrl();
  return (
    <div data-testid="register-mua-page" className="bg-white">
      {/* Hero */}
      <section className="bg-[#eb5381] text-white pt-32 pb-20 md:pb-28">
        <div className="container-edge grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] opacity-90">
              <Sparkles className="w-4 h-4" /> For Artists
            </div>
            <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Get <span className="italic">discovered</span> by brides actively looking to book.
            </h1>
            <p className="mt-7 max-w-xl text-lg text-white/90 leading-relaxed">
              India's most curated beauty marketplace — built to send you booking-ready enquiries, not browsers.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="/register"
                data-testid="hero-register-cta"
                className="inline-flex items-center justify-center gap-3 bg-black hover:bg-white hover:text-[#eb5381] text-white px-10 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors"
              >
                Register Now <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={`mailto:${BRAND.email}?subject=Artist%20Onboarding%20Question`}
                className="inline-flex items-center justify-center gap-3 border border-white hover:bg-white hover:text-[#eb5381] text-white px-10 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors"
              >
                Ask a Question
              </a>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="bg-white text-black p-7">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Limited Time</div>
              <div className="mt-3 font-serif text-3xl leading-tight">No Registration Charges<br/>for Early Artists</div>
              <ul className="mt-5 space-y-2 text-sm text-neutral-700">
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#eb5381] shrink-0 mt-0.5" /> Premium portfolio page</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#eb5381] shrink-0 mt-0.5" /> Verified badge</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#eb5381] shrink-0 mt-0.5" /> Direct WhatsApp leads</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#eb5381] shrink-0 mt-0.5" /> Editorial features</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits grid */}
      <section className="container-edge py-20 md:py-28">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Why Find Your MUA</div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Built for <span className="italic text-[#a465a8]">artists</span>, by people who love beauty.
          </h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#eaeaea]">
          {BENEFITS.map((b, i) => (
            <div key={b.t} data-testid={`benefit-${i}`} className="bg-white p-8 lg:p-10 hover:bg-[#fff8ee] transition-colors">
              <div className="font-serif text-4xl text-[#eb5381]">0{i + 1}.</div>
              <div className="mt-5 font-serif text-2xl leading-snug">{b.t}</div>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="bg-[#ffefd1] py-20 md:py-28">
        <div className="container-edge">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">How Onboarding Works</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                From application to <span className="italic text-[#a465a8]">first booking,</span> in days.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-neutral-700 text-base md:text-lg max-w-md leading-relaxed">
                We move fast for talent. Most artists are live on the platform within a week of applying.
              </p>
            </div>
          </div>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#eaeaea]">
            {STEPS.map((s) => (
              <li key={s.n} data-testid={`step-${s.n}`} className="bg-white p-7 lg:p-8 min-h-[200px] flex flex-col justify-between">
                <div className="font-serif text-5xl text-[#eb5381]">{s.n}</div>
                <div>
                  <div className="font-serif text-xl leading-tight">{s.t}</div>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0a0a0a] text-white py-24 md:py-32">
        <div className="container-edge max-w-4xl text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Ready When You Are</div>
          <h2 className="mt-4 font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05]">
            Your brides are <span className="italic text-[#ed9393]">already searching.</span>
          </h2>
          <p className="mt-7 max-w-xl mx-auto text-base md:text-lg text-neutral-300 leading-relaxed">
            Spend 3 minutes on the form. Our team takes it from there.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/register"
              data-testid="footer-register-cta"
              className="inline-flex items-center justify-center gap-3 bg-[#eb5381] hover:bg-[#d64570] text-white px-12 py-5 text-[12px] uppercase tracking-[0.25em] transition-colors"
            >
              Open Registration Form <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <p className="mt-6 text-xs text-neutral-500">Hosted securely on Google Forms. Takes ~3 minutes.</p>
        </div>
      </section>
    </div>
  );
}
